import type { ApiError } from "../lib/api";
import { NeuralAnimation } from "./NeuralAnimation";

/**
 * Loading, error and empty states.
 *
 * content/voice-guide.md is specific about all three: errors explain what
 * happened and what to do without apologising, and empty states are
 * invitations rather than apologies. "No projects found." is the wrong
 * string; "No projects match that filter. Clear it to see all ten." is right.
 *
 * Loading shares its drawing with the landing sequence rather than using a
 * spinner. It costs nothing extra — the component is already in the bundle —
 * and it means the site has one visual idea for "working" instead of a
 * signature animation on arrival and a generic circle everywhere after.
 */

export function Loading({ label }: { label: string }) {
  return (
    // role="status" on the wrapper, not the animation: a screen reader needs
    // the words. The drawing is aria-hidden and cannot say what is loading.
    <div role="status" className="flex flex-col items-start gap-4 py-6">
      <NeuralAnimation loop layerDuration={0.5} className="w-28 opacity-80" />
      <p className="text-text-dim">Loading {label}…</p>
    </div>
  );
}

export function Failed({ error, what }: { error: ApiError; what: string }) {
  // A sleeping free-tier backend and a genuinely missing page need different
  // words. The first is worth waiting for; the second never resolves.
  const message = error.isUnavailable
    ? `Couldn't load ${what}. The server may be waking up — reload in a few seconds.`
    : error.message;

  return (
    <div role="alert" className="max-w-(--spacing-measure)">
      <p className="mb-2 text-text">{message}</p>
      <p className="text-2xs text-text-dim">
        If it keeps happening,{" "}
        <a
          className="text-text underline decoration-edge underline-offset-4 hover:decoration-accent"
          href="mailto:iftikhar.aiwork@gmail.com"
        >
          iftikhar.aiwork@gmail.com
        </a>{" "}
        reaches Iftikhar directly.
      </p>
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="max-w-(--spacing-measure) text-text-dim">{children}</p>;
}
